/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Hightouch.Substitutions = NO -->*/
/*Allow usage of "Warehouse planning" just this once*/
/*<!-- vale Hightouch.Substitutions  = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Lightning sync engine", description = "Use the Lightning sync engine to speed up sync performance by storing change data capture in your data warehouse rather than on Hightouch managed infrastructure.", lastUpdatedDate = new Date(1742774400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Lightning sync engine",
        description: "Use the Lightning sync engine to speed up sync performance by storing change data capture in your data warehouse rather than on Hightouch managed infrastructure.",
        lastUpdatedDate: "Mon Mar 24 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Engine comparison",
          "depth": 1,
          "id": "engine-comparison"
        }, {
          "value": "Supported sources",
          "depth": 0,
          "id": "supported-sources"
        }, {
          "value": "Enable the Lightning sync engine",
          "depth": 0,
          "id": "enable-the-lightning-sync-engine"
        }, {
          "value": "Warehouse schemas",
          "depth": 1,
          "id": "warehouse-schemas"
        }, {
          "value": "Migrate from Basic sync engine",
          "depth": 0,
          "id": "migrate-from-basic-sync-engine"
        }, {
          "value": "Migration tips",
          "depth": 1,
          "id": "migration-tips"
        }, {
          "value": "Common errors",
          "depth": 0,
          "id": "common-errors"
        }, {
          "value": "Primary key is not unique",
          "depth": 1,
          "id": "primary-key-is-not-unique"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      strong: "strong",
      a: "a",
      img: "img",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      ul: "ul",
      li: "li",
      ol: "ol",
      code: "code",
      h4: "h4",
      pre: "pre",
      span: "span",
      em: "em"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsx(_components.p, {
          children: "The Lightning sync engine was previously known as warehouse planning. Both terms describe the same feature."
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch identifies the incremental changes in your data model so that syncs send only the necessary updates to your destinations. For every sync, Hightouch computes what rows to send by pulling all rows within your data model and comparing them with the last sync. This process is called ", _jsx(_components.strong, {
          children: "change data capture"
        }), " (CDC) or ", _jsx(_components.strong, {
          children: "diffing."
        }), " You can find more details in the ", _jsx(_components.a, {
          href: "/getting-started/concepts#change-data-capture",
          children: "core concepts"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "By default, the change data capture computation and file storage happens on Hightouch managed infrastructure. This can be slow for large models that take a long time to query."
      }), "\n", _jsxs(_components.p, {
        children: ["With the ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), ", Hightouch computes change data capture ", _jsx(_components.strong, {
          children: "directly in your warehouse"
        }), ". It stores previously synced data into a special schema managed by Hightouch. Customers with extensive datasets requiring high performance syncs typically use the Lightning sync engine."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/basic-sync-engine.png",
          alt: "Basic sync architecture diagram"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/lightning-sync-engine.png",
          alt: "Lightning sync engine architecture diagram"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Hightouch recommends using the Lightning sync engine for ", _jsx(_components.a, {
            href: "#supported-sources",
            children: "supported sources"
          }), " when syncing more than 100 thousand rows of data."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "engine-comparison",
        children: "Engine comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), " requires granting write access to your data warehouse, which makes its setup more involved than the Basic sync engine. However, it is more performant and reliable than the Basic engine. This makes it the ideal choice to guarantee faster syncs, especially with large data models. It also supports more features, such as ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), ", ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), ", and ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/identity-resolution",
          children: "Identity Resolution"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Criteria"
            }), _jsx(_components.th, {
              children: "Basic sync engine"
            }), _jsx(_components.th, {
              children: "Lightning sync engine"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Performance"
            }), _jsx(_components.td, {
              children: "Slower"
            }), _jsx(_components.td, {
              children: "Quicker (up to 100 times faster)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Ideal for large data models (over 100 thousand rows)"
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "Yes"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Reliability"
            }), _jsx(_components.td, {
              children: "Normal"
            }), _jsx(_components.td, {
              children: "High"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Resilience to sync interruptions"
            }), _jsx(_components.td, {
              children: "Normal"
            }), _jsx(_components.td, {
              children: "High"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Extra features"
            }), _jsx(_components.td, {
              children: "None"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/syncs/warehouse-sync-logs",
                children: "Warehouse Sync Logs"
              }), ", ", _jsx(_components.a, {
                href: "/match-booster/overview",
                children: "Match Booster"
              }), ", ", _jsx(_components.a, {
                href: "https://hightouch.com/platform/identity-resolution",
                children: "Identity Resolution"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Ease of setup"
            }), _jsx(_components.td, {
              children: "Simpler"
            }), _jsx(_components.td, {
              children: "More involved"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Location of change data capture"
            }), _jsx(_components.td, {
              children: "Hightouch infrastructure"
            }), _jsx(_components.td, {
              children: "Warehouse schemas managed by Hightouch"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Required warehouse permissions"
            }), _jsx(_components.td, {
              children: "Read-only"
            }), _jsx(_components.td, {
              children: "Read and write"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Ability to switch"
            }), _jsx(_components.td, {
              children: "You can move to the Lightning engine at any time"
            }), _jsx(_components.td, {
              children: "You can't move to the Basic engine once Lightning is configured"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "supported-sources",
        children: "Supported sources"
      }), "\n", _jsx(_components.p, {
        children: "The Lightning sync engine works with the following sources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/synapse",
            children: "Azure Synapse"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/greenplum",
            children: "Greenplum Database"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/fabric",
            children: "Microsoft Fabric"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/trino",
            children: "Trino"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "enable-the-lightning-sync-engine",
        children: "Enable the Lightning sync engine"
      }), "\n", _jsxs(_components.p, {
        children: ["When first setting up one of the ", _jsx(_components.a, {
          href: "#supported-sources",
          children: "supported sources"
        }), ", Hightouch prompts you to ", _jsx(_components.strong, {
          children: "Choose your sync engine"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/choose-your-engine.png",
          alt: "Choosing your sync engine in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can set up the Lightning sync engine at this point or continue with the Basic sync engine and ", _jsx(_components.a, {
          href: "#migrate-from-basic-sync-engine",
          children: "upgrade to the Lightning engine"
        }), " later. Once you've configured the Lightning engine, you can't switch back to the Basic engine."]
      }), "\n", _jsx(_components.p, {
        children: "In addition to enabling the Lightning sync engine in the Hightouch UI, you need to:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Grant Hightouch write permission to your data warehouse."
        }), "\n", _jsx(_components.li, {
          children: "Create the warehouse schemas for Hightouch to use."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Refer to the source-specific in-app instructions on how to complete these steps."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/source-specific-setup.png",
          alt: "Choosing your sync engine in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "warehouse-schemas",
        children: "Warehouse schemas"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch requires you to create two schemas:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "hightouch_planner"
          }), ": Hightouch uses this schema to power the Lightning sync engine."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "hightouch_audit"
          }), ": Hightouch uses this schema to power ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "Warehouse Sync Logs"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Whenever a sync that uses the Lightning sync engine runs, Hightouch creates two new tables in the ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " schema: one to log the model's query results (the ", _jsx(_components.code, {
          children: "_plan"
        }), " table) and one to log rows rejected during the sync (the ", _jsx(_components.code, {
          children: "_rejections"
        }), " table).\nHightouch uses these tables to perform ", _jsx(_components.a, {
          href: "#overview",
          children: "change data capture"
        }), " and plan which rows to sync."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch only keeps the two most recent pairs of tables to compute the diff for the current sync.\nIn other words, Hightouch ", _jsx(_components.a, {
          href: "/getting-started/concepts#does-hightouch-keep-historical-records-of-change-data-capture",
          children: "doesn't maintain historical records"
        }), " of these tables, and any given sync never has more than the two most recent pairs of tables.\nBecause these tables and their names change with every sync run, Hightouch requires permission to write to the entire ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " schema rather than specific tables."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You can learn more about the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema on the ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs#schema",
            children: "Warehouse Sync Logs"
          }), " page."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "table-removal",
        children: "Table removal"
      }), "\n", _jsxs(_components.p, {
        children: ["Removing tables from the ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " and ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " schemas isn't recommended:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "hightouch_planner"
          }), ": these tables are required for ", _jsx(_components.a, {
            href: "#overview",
            children: "change data capture"
          }), " to work correctly; if you delete these tables, you need to trigger a ", _jsx(_components.a, {
            href: "/syncs/overview#resync-full-query",
            children: "full resync"
          }), " to fix your syncs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "hightouch_audit"
          }), ": removing tables from the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema would mean deleting the ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "Warehouse Sync Logs"
          }), " related to a sync's prior runs, which are useful for historical analysis."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If your ", _jsx(_components.a, {
          href: "#supported-sources",
          children: "source"
        }), " is located in the European Union, you might want to avoid having PII older than 30 days in your data warehouse, in accordance with the ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/General_Data_Protection_Regulation",
          children: "GDPR"
        }), ".\nIn this case, we recommend running ", _jsx(_components.strong, {
          children: "all"
        }), " syncs in your workspace at least once every 15 days.\nThis ensures that Hightouch refreshes the tables in the ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " schema and the ", _jsx(_components.strong, {
          children: "Snapshot"
        }), " table in the ", _jsxs(_components.a, {
          href: "/syncs/warehouse-sync-logs#schema",
          children: [_jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema"]
        }), ", removing all older PII before it reaches the 30-day threshold."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Historical data is never automatically cleared out from the ", _jsx(_components.strong, {
            children: "Changelog"
          }), " table in the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema.\nIf required, you need to manually remove older PII from this table."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.strong, {
            children: "Sync runs"
          }), " table in the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema doesn't contain PII."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "migrate-from-basic-sync-engine",
        children: "Migrate from Basic sync engine"
      }), "\n", _jsxs(_components.p, {
        children: ["You can migrate to the Lightning sync engine by going to the ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab of any previously setup ", _jsx(_components.a, {
          href: "#supported-sources",
          children: "supported source"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the source's ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab, select ", _jsx(_components.strong, {
            children: "Lightning sync engine"
          }), " under ", _jsx(_components.strong, {
            children: "Choose your sync engine"
          }), " ."]
        }), "\n", _jsx(_components.li, {
          children: "Follow the source-specific in-app instructions to grant Hightouch write permissions to your warehouse and create the schemas for Hightouch to use."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Test your connection to ensure setup is correct."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/lightning-engine-migration.png",
          alt: "Choosing your sync engine in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you enable the Lightning sync engine on an existing source, Hightouch migrates any syncs on the Basic engine to the Lightning sync engine on the next run. Any previously synced data won't be unnecessarily resynced since the existing ", _jsx(_components.a, {
          href: "#overview",
          children: "change data capture"
        }), " data is copied over to your warehouse schemas. In most cases you don't need to trigger ", _jsx(_components.a, {
          href: "/syncs/overview#resync-full-query",
          children: "full resyncs"
        }), " or ", _jsx(_components.a, {
          href: "/syncs/overview#reset-cdc",
          children: "reset change data capture (CDC)"
        }), ", unless you ", _jsx(_components.a, {
          href: "#migration-tips",
          children: "encounter errors"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The first run after migrating to the Lightning sync engine can take longer than usual, as it uses a special migration sync engine to create the necessary ", _jsx(_components.a, {
            href: "#warehouse-schemas",
            children: "schemas"
          }), " in your warehouse."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "migration-tips",
        children: "Migration tips"
      }), "\n", _jsx(_components.p, {
        children: "Follow these tips when performing the migration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The Lightning sync engine requires your ", _jsx(_components.a, {
            href: "#primary-key-is-not-unique",
            children: "primary key to be unique"
          }), " for every model using the source. If you're unsure if your primary key is unique, check before migrating."]
        }), "\n", _jsxs(_components.li, {
          children: ["If you have a large number of syncs to migrate, please\n", _jsx(PylonLink, {
            pylonText: "Hello, I'm migrating to the Lightning sync engine and have a large number of syncs. Could you please help us migrate incrementally?",
            linkText: "reach out"
          }), " so that we can help you migrate incrementally."]
        }), "\n", _jsxs(_components.li, {
          children: ["If you hit any errors, you can often fix them with a ", _jsx(_components.a, {
            href: "/syncs/overview#resync-full-query",
            children: "full resync"
          }), ". Ensure your sync meets the ", _jsx(_components.a, {
            href: "/syncs/overview#full-resync-prerequisites",
            children: "full resync prerequisites"
          }), " before doing so. Otherwise, you can ", _jsx(_components.a, {
            href: "/syncs/overview#reset-cdc",
            children: "reset change data capture (CDC)"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(_components.h3, {
        id: "primary-key-is-not-unique",
        children: "Primary key is not unique"
      }), "\n", _jsxs(_components.p, {
        children: ["The Lightning sync engine requires that every row in your model has a unique value for its ", _jsx(_components.a, {
          href: "/getting-started/concepts#unique-primary-key-requirement",
          children: "primary key"
        }), ". This is because the primary key is used to uniquely identify each row, and for example, whether it failed previously."]
      }), "\n", _jsxs(_components.p, {
        children: ["Models with duplicate primary keys can be deduplicated in SQL with the ", _jsx(_components.code, {
          children: "ROW_NUMBER()"
        }), " function."]
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "WITH"
          }), " your_data_model ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "copy"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), "paste your model here\n)\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WITH"
          }), " your_data_model_with_rank ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ", ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "ROW_NUMBER"
          }), "() ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OVER"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "PARTITION"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " your_primary_key) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " rank ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " your_data_model\n)\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " your_data_model_with_rank ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " rank", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The preceding example de-duplicates your model by ", _jsx(_components.em, {
          children: "partitioning"
        }), " on the primary key. It chooses an arbitrary record from your duplicates. You can use ", _jsx(_components.code, {
          children: "ORDER BY"
        }), " or ", _jsx(_components.code, {
          children: "WHERE"
        }), " to filter and select which data from the duplicates you want to keep."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
